import { Accordion } from '@components/accordion/Accordion';
import { Markdown } from '@components/markdown/Markdown';

import { formatPrice } from '@common/utils/price';
import { getEventDescription } from '@common/utils/programme';
import {
	AccordionItemRecord,
	InfoBlockRecord,
	SupabaseEventRecord,
} from '@interfaces';

interface ListingDetailPrimaryInformationProps {
	event: SupabaseEventRecord;
	infoBlocks: InfoBlockRecord[];
	className?: string;
}

export const ListingDetailPrimaryInformation = ({
	event,
	infoBlocks,
	className,
}: ListingDetailPrimaryInformationProps) => {
	const accordionItems: AccordionItemRecord[] = [];
	const eventDescription = getEventDescription({ event });

	if (eventDescription) {
		accordionItems.push({
			id: 'event-description',
			isInitiallyExpanded: true,
			summary: <div>Event description</div>,
			panel: (
				<div>
					<Markdown text={eventDescription} />
				</div>
			),
		});
	}

	if (event.artists.length) {
		accordionItems.push({
			id: 'event-artists',
			isInitiallyExpanded: true,
			summary: <div>Artists</div>,
			panel: (
				<ul className="list-unstyled">
					{event.artists.map((artist) => (
						<li
							itemProp="performer"
							itemScope
							itemType="https://schema.org/MusicGroup"
							key={artist}
						>
							<span itemProp="name">{artist}</span>
						</li>
					))}
				</ul>
			),
		});
	}

	if (event.tickets.length) {
		accordionItems.push({
			id: 'event-tickets',
			summary: <div>Tickets</div>,
			panel: (
				<ul className="list-unstyled">
					{event.tickets.map((ticket) => (
						<li key={Object.values(ticket).join('-')}>
							<p>
								{ticket.name} — {formatPrice({ cents: ticket.total })}
							</p>
							{ticket.fees > 0 ? (
								<p>
									({formatPrice({ cents: ticket.face_value })} Face value +{' '}
									{formatPrice({ cents: ticket.fees })} Fees)
								</p>
							) : null}
						</li>
					))}
				</ul>
			),
		});
	}

	infoBlocks.forEach(({ id, heading, text }) => {
		accordionItems.push({
			id,
			summary: <div>{heading}</div>,
			panel: <Markdown text={text} />,
		});
	});

	return (
		<div className={className}>
			<Accordion buttonWrapper="h2" items={accordionItems} />
		</div>
	);
};
